import React, {createRef, PropsWithChildren, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {TieredMenu} from "primereact/tieredmenu";
import DomHelper from "../helper/DomHelper";
import {MenuItem} from "primereact/menuitem";
import FunctionHelper from "../helper/FunctionHelper";
import {MenuButtonProperties} from "./MenuButtonInterface";
import PortalComponent from "../components/PortalComponent";

/**
 * A MenuButton component is used to represent a Button with an attached optional {@link Menu} which can also be added
 * as dropdown with a split button.
 */
export default class MenuButton extends PortalComponent<PropsWithChildren<MenuButtonProperties>, any> {
	private menu: RefObject<TieredMenu>;
	private menuId: string;

	/**
	 * Constructs a {@link MenuButton} component
	 */
	constructor(props: Readonly<PropsWithChildren<MenuButtonProperties>>) {
		super(props);

		this.menu = createRef<TieredMenu>();
		this.menuId = DomHelper.generateUUID();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let classNames: Array<string> = [];

		if (FunctionHelper.exists(this.props.className)) {
			for (let className of this.props.className!.split(" ")) {
				classNames.push(className);
			}
		}

		let menuItems: Array<MenuItem> = [];
		for (let item of FunctionHelper.applyValue(this.props.menuItems, [])) {
			menuItems.push({
				...item,
				command: async (event) => {
					if (FunctionHelper.applyValue(item.items, []).length === 0 && !this.props.keepOpen) {
						this.menu.current?.toggle(event.originalEvent);
					}

					if (FunctionHelper.exists(item.command)) {
						await item.command(event);
					}
				}
			})
		}

		return <>
			{this.props.splitButton ?
				<div className={classNames.concat(["p-splitbutton", "p-component"]).join(" ")}
					 data-help={this.props["data-help"]} data-position={this.props["data-position"]}>
					<Button className="p-button p-component p-splitbutton-defaultbutton tooltip"
							icon={this.props.icon} label={this.props.label} onClick={this.props.onClick}
							data-pr-tooltip={!this.props.disabled ? this.props.tooltip : undefined}
							data-pr-position={this.props.tooltipOptions?.position} disabled={this.props.disabled}>
						{this.props.children}
					</Button>
					<Button className="p-button p-component p-splitbutton-menubutton p-button-icon-only"
							disabled={this.props.disabled} icon="icon icon-angle-down"
							aria-haspopup={FunctionHelper.exists(this.props.menuItems)}
							aria-controls={FunctionHelper.exists(this.props.menuItems) ? this.menuId : undefined}
							onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
								this.menu.current!.toggle(event);
							}}/>
				</div> :
				<Button className={classNames.concat("tooltip").join(" ")} disabled={this.props.disabled}
						label={this.props.label} style={this.props.style} icon={this.props.icon}
						aria-haspopup={FunctionHelper.exists(this.props.menuItems)} badge={this.props.badge}
						aria-controls={FunctionHelper.exists(this.props.menuItems) ? this.menuId : undefined}
						data-pr-tooltip={!this.props.disabled ? this.props.tooltip : undefined}
						data-pr-position={this.props.tooltipOptions?.position}
						data-help={this.props["data-help"]} data-position={this.props["data-position"]}
						onClick={
							(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
								if (FunctionHelper.exists(this.props.menuItems)) {
									this.menu.current!.toggle(event);
								}

								if (FunctionHelper.exists(this.props.onClick)) {
									this.props.onClick!(event);
								}
							}
						}>
					{this.props.children}
				</Button>
			}
			{FunctionHelper.exists(this.props.menuItems) &&
				<TieredMenu popup={true} ref={this.menu} id={this.menuId} className={this.props.menuClassName}
							appendTo={document.getElementById("root")} model={menuItems}/>
			}
		</>
	}
}